/**
 * TrackerTableSkeleton — the section-level loading state every Tracker table
 * shows while its page of rows is in flight (DESIGN.md §15.1: a card inside an
 * already-rendered page gets a skeleton matching the post-load layout, never a
 * spinner and never a static grey block).
 *
 * Extracted on the third copy: `TrackerOrderTable` and `TrackerQueueTable` each
 * carry a hand-inlined version of exactly this markup, differing only in how
 * many `<Skeleton>` bars they draw and how wide each one is. Those two are left
 * untouched here deliberately — migrating them is a mechanical follow-up that
 * would otherwise ride along inside a feature PR.
 *
 * `widths` is one Tailwind width class per column, in column order, so the
 * shimmer lands under the real headers instead of a generic five-bar smear.
 */

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** Five rows is the established Tracker rhythm — enough to read as a table. */
const SKELETON_ROWS = [0, 1, 2, 3, 4];

export function TrackerTableSkeleton({ widths }: { widths: readonly string[] }) {
  return (
    <div className="rounded-md border">
      <div className="space-y-0">
        {SKELETON_ROWS.map((row) => (
          <div key={row} className="flex gap-4 py-3.5 px-4 border-b last:border-0">
            {widths.map((width, column) => (
              <Skeleton key={column} className={cn('h-6', width)} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
